import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, scrollToTop } from "@/lib/api";

const LINKS = [
  { id: "products", label: "Products", testid: "navbar-link-products" },
  { id: "why", label: "Why SRS", testid: "navbar-link-trust" },
  { id: "stories", label: "Stories", testid: "navbar-link-stories" },
  { id: "projects", label: "Portfolio", testid: "navbar-link-gallery" },
  { id: "contact", label: "Contact", testid: "navbar-link-contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.82);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const overHero = location.pathname === "/" && !scrolled;

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      scrollToId(id);
    }
  };

  const toTop = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    scrollToTop();
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${overHero ? "text-white" : "text-ink"}`}
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <button onClick={toTop} data-testid="navbar-logo" className="flex items-center gap-3" aria-label="Back to top">
            <img
              src={overHero ? "/assets/logo-white.png" : "/assets/logo.png"}
              alt="SRS – Sun Roofing Solutions"
              className="h-8 w-8 object-contain"
            />
            <span className="hidden font-display text-sm font-semibold tracking-[0.14em] sm:block"></span>
          </button>

          <nav className="hidden items-center gap-9 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={l.testid}
                onClick={() => go(l.id)}
                className={`text-[13px] font-medium transition-colors ${
                  overHero ? "text-white/80 hover:text-white" : "text-ink/70 hover:text-ink"
                }`}
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="navbar-call-button"
              onClick={() => go("contact")}
              className={`rounded-full px-5 py-2.5 text-[13px] font-medium transition-colors ${
                overHero ? "bg-white text-ink hover:bg-srs hover:text-white" : "bg-ink text-white hover:bg-srs"
              }`}
            >
              Get a Free Estimate
            </button>
          </nav>

          <button
            data-testid="navbar-menu-button"
            onClick={() => setOpen(true)}
            className="p-2 lg:hidden"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="navbar-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink text-white"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <button onClick={toTop} aria-label="Back to top">
                <img src="/assets/logo-white.png" alt="SRS" className="h-9 w-9 object-contain" />
              </button>
              <button data-testid="navbar-menu-close" onClick={() => setOpen(false)} className="p-2" aria-label="Close menu">
                <X size={24} />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`navbar-mobile-${l.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => go(l.id)}
                  className="border-b border-white/10 py-5 text-left font-display text-4xl font-semibold tracking-tight hover:text-srs"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
            <div className="px-8 pb-12">
              <button
                onClick={() => go("contact")}
                className="w-full rounded-full bg-white py-4 text-sm font-semibold text-ink transition-colors hover:bg-srs hover:text-white"
              >
                Get a Free Estimate
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
